import { useEffect, useState, useCallback } from 'react';
import { automationApi, type CampaignRecord } from '@/lib/adminApi';
import { toast, Toaster } from 'sonner';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { StatCard, PageHeader, Skeleton, EmptyState } from '@/components/ui/page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RefreshCw, Plus, Play, Pause, Megaphone, BarChart3, Users } from 'lucide-react';

const segmentOptions = [
  'new_customer', 'returning_customer', 'high_value', 'repeat_buyer',
  'returned_orders', 'inactive', 'vip',
];

export default function AutomationCampaignsPage() {
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '', campaign_type: 'cross_sell', trigger_days_after_delivery: 7,
    target_segments: [] as string[], max_recipients_per_day: 100,
  });

  const load = useCallback(async () => {
    try {
      const res = await automationApi.campaigns();
      if (res.success) setCampaigns(res.data);
    } catch (e: any) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await automationApi.createCampaign(newCampaign);
      toast.success('Campaign created');
      setShowCreate(false);
      setNewCampaign({ name: '', campaign_type: 'cross_sell', trigger_days_after_delivery: 7, target_segments: [], max_recipients_per_day: 100 });
      load();
    } catch (e: any) { toast.error(e.message); }
  };

  const toggleSegment = (seg: string) => {
    setNewCampaign(prev => ({
      ...prev,
      target_segments: prev.target_segments.includes(seg)
        ? prev.target_segments.filter(s => s !== seg)
        : [...prev.target_segments, seg],
    }));
  };

  const activate = async (id: string) => {
    try { await automationApi.activateCampaign(id); toast.success('Campaign activated'); load(); }
    catch (e: any) { toast.error(e.message); }
  };
  const pause = async (id: string) => {
    try { await automationApi.pauseCampaign(id); toast.success('Campaign paused'); load(); }
    catch (e: any) { toast.error(e.message); }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <PageHeader title="Campaign Manager" description="Post-delivery marketing campaigns" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  const totalSent = campaigns.reduce((s, c) => s + c.total_sent, 0);
  const totalRevenue = campaigns.reduce((s, c) => s + (c.revenue_generated || 0), 0);

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Toaster position="top-center" richColors />
      <PageHeader
        title="Campaign Manager"
        description="Create and manage post-delivery WhatsApp marketing campaigns"
        action={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
            <Button size="sm" onClick={() => setShowCreate(!showCreate)}><Plus className="h-3.5 w-3.5 mr-1" /> New Campaign</Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Campaigns" value={campaigns.length} />
        <StatCard label="Total Sent" value={totalSent.toLocaleString()} />
        <StatCard label="Revenue" value={`EGP ${totalRevenue.toLocaleString()}`} />
        <StatCard label="Active" value={campaigns.filter(c => c.status === 'active').length} />
      </div>

      {/* Create Campaign Form */}
      {showCreate && (
        <Card>
          <CardHeader title="New Campaign" />
          <CardBody>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Campaign Name</label>
                <Input value={newCampaign.name} onChange={e => setNewCampaign(p => ({ ...p, name: e.target.value }))} placeholder="Summer sale cross-sell" className="h-9" />
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Type</label>
                <select value={newCampaign.campaign_type} onChange={e => setNewCampaign(p => ({ ...p, campaign_type: e.target.value }))}
                  className="w-full h-9 rounded-lg bg-elevated border border-white/[0.08] text-sm text-foreground px-3 focus:outline-none focus:ring-1 focus:ring-white/20">
                  <option value="cross_sell">Cross-sell</option>
                  <option value="reorder_reminder">Reorder Reminder</option>
                  <option value="win_back">Win-back</option>
                  <option value="vip_offer">VIP Offer</option>
                  <option value="seasonal">Seasonal</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Days After Delivery</label>
                <Input type="number" value={newCampaign.trigger_days_after_delivery}
                  onChange={e => setNewCampaign(p => ({ ...p, trigger_days_after_delivery: parseInt(e.target.value) || 7 }))}
                  className="h-9" />
              </div>
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Max Recipients/Day</label>
                <Input type="number" value={newCampaign.max_recipients_per_day}
                  onChange={e => setNewCampaign(p => ({ ...p, max_recipients_per_day: parseInt(e.target.value) || 100 }))}
                  className="h-9" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-zinc-400 block mb-1">Target Segments</label>
                <div className="flex flex-wrap gap-1.5">
                  {segmentOptions.map(seg => (
                    <button key={seg} onClick={() => toggleSegment(seg)}
                      className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                        newCampaign.target_segments.includes(seg)
                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                          : 'bg-elevated text-zinc-400 border border-white/[0.06] hover:border-white/[0.12]'
                      }`}>
                      {seg.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" onClick={create} disabled={!newCampaign.name}><Plus className="h-3.5 w-3.5 mr-1" /> Create</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Campaigns List */}
      <Card>
        <CardHeader title="All Campaigns" />
        <CardBody>
          {campaigns.length === 0 ? (
            <EmptyState title="No campaigns yet. Create one above." />
          ) : (
            <div className="space-y-2">
              {campaigns.map(c => (
                <div key={c.id} className="flex items-center justify-between p-3 rounded-lg bg-elevated border border-white/[0.04]">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Megaphone className="h-3.5 w-3.5 text-zinc-500" />
                      <span className="font-medium text-sm">{c.name}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        c.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' :
                        c.status === 'paused' ? 'bg-amber-500/10 text-amber-400' :
                        'bg-zinc-500/10 text-zinc-400'
                      }`}>{c.status}</span>
                    </div>
                    <div className="flex gap-3 mt-1 text-xs text-zinc-500">
                      <span>Type: {c.campaign_type.replace(/_/g, ' ')}</span>
                      <span>Trigger: {c.trigger_days_after_delivery}d after delivery</span>
                      <span>Sent: {c.total_sent}/{c.total_recipients}</span>
                      <span>Delivered: {c.total_delivered}</span>
                      <span>Read: {c.total_read}</span>
                      {c.revenue_generated > 0 && <span className="text-emerald-400">Revenue: EGP {c.revenue_generated.toLocaleString()}</span>}
                    </div>
                    {c.target_segments?.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {c.target_segments.map(s => (
                          <span key={s} className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 text-xs">{s.replace(/_/g, ' ')}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 ml-3">
                    {c.status === 'draft' && (
                      <Button size="sm" variant="outline" onClick={() => activate(c.id)}><Play className="h-3 w-3 mr-1" /> Activate</Button>
                    )}
                    {c.status === 'active' && (
                      <Button size="sm" variant="outline" onClick={() => pause(c.id)}><Pause className="h-3 w-3 mr-1" /> Pause</Button>
                    )}
                    {c.status === 'paused' && (
                      <Button size="sm" variant="outline" onClick={() => activate(c.id)}><Play className="h-3 w-3 mr-1" /> Resume</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
